<script setup>
import { ref, watch, computed, onMounted } from 'vue';
import InputText from 'primevue/inputtext';
import IconField from 'primevue/iconfield';
import InputIcon from 'primevue/inputicon';
import { Link, Head, usePage, router } from '@inertiajs/vue3';
import { Dialog, DialogPanel, Disclosure, DisclosureButton, DisclosurePanel,Menu, MenuButton, MenuItem, MenuItems, Popover, PopoverButton, PopoverGroup, PopoverPanel, TransitionChild, TransitionRoot, } from '@headlessui/vue'
import Paginator from 'primevue/paginator';
import axios from 'axios';
import moment from 'moment';

let eurFormatter = new Intl.NumberFormat('nl-NL', {
  style: 'currency',
  currency: 'EUR',
  minimumFractionDigits: 2,
  maximumFractionDigits: 2
});

const page = usePage();
const user = page.props.auth.user;

const props = defineProps({ 
    lots: Array,
    brands: Array,
    categories: Array,
});

const pageLots = computed(() => page.props.lots);
const currentPage = ref(0);


// const activeStatusFilters = ref([])
const activeCategoryFilters = ref(getFilterFromUrl('category'))
const activeBrandFilters = ref(getFilterFromUrl('brand'))
const activeMileageFilters = ref(getFilterFromUrl('mileage'))
const activeYearFilters = ref(getFilterFromUrl('year').map(y => parseInt(y)))
const activeTransmissionFilters = ref(getFilterFromUrl('transmission'))
const activeFuelFilters = ref(getFilterFromUrl('fuel'))
const activeColorFilters = ref(getFilterFromUrl('color'))
const activePriceFilters = ref(getFilterFromUrl('price'))

// Get initial page from URL if available
const urlParams = new URLSearchParams(window.location.search);
// Add a reactive reference for the current sort
const currentSort = ref(urlParams.get('sort') || '');

if(urlParams.has('page')) {
    // URL uses 1-based pagination, convert to 0-based for internal use
    currentPage.value = parseInt(urlParams.get('page')) - 1;
}

watch(pageLots, () => {
    lots.value = pageLots.value;
    lots.value = doFilter();
    //set paginator to page 1
    if (paginator.value) {
        paginator.value.changePage(0);
    }
})

// Helper functions for URL params
function getFilterFromUrl(paramName) {
    const params = new URLSearchParams(window.location.search);
    const value = params.get(paramName);

    return value ? value.split(',') : [];
}

function updateUrlWithFilters() {
    // Create props object for router
    const parameters = {};
    
    // Update page parameter (convert 0-based to 1-based)
    if (currentPage.value !== 0) {
        parameters.page = currentPage.value + 1;
    }
    
    // Add filter parameters
    if (activeCategoryFilters.value.length > 0) {
        parameters.category = activeCategoryFilters.value.join(',');
    }
    
    if (activeBrandFilters.value.length > 0) {
        parameters.brand = activeBrandFilters.value.join(',');
    }
    
    if (activeMileageFilters.value.length > 0) {
        parameters.mileage = activeMileageFilters.value.join(',');
    }
    
    if (activeYearFilters.value.length > 0) {
        parameters.year = activeYearFilters.value.join(',');
    }
    
    if (activeTransmissionFilters.value.length > 0) {
        parameters.transmission = activeTransmissionFilters.value.join(',');
    }
    
    if (activeFuelFilters.value.length > 0) {
        parameters.fuel = activeFuelFilters.value.join(',');
    }
    
    if (activeColorFilters.value.length > 0) {
        parameters.color = activeColorFilters.value.join(',');
    }
    
    if (activePriceFilters.value.length > 0) {
        parameters.price = activePriceFilters.value.join(',');
    }
    
    // Preserve sort parameter if exists
    const sort = urlParams.get('sort');
    if (sort) {
        parameters.sort = sort;
    }

    //generate the new url
    const newUrl = new URL(window.location.href);
    newUrl.search = new URLSearchParams(parameters).toString();

    // Push the new URL to the history stack
    router.push({
        url: newUrl.pathname + newUrl.search,
        replace: true,
    })
}

const lots = ref(props.lots);
const paginator = ref(null);
const paginatedData = ref([]);
const searchValue = ref('');

watch(searchValue, (newValue) => {
    lots.value = doFilter();
});

function changePagination(pageState) {
    //get row count
    const rows = pageState.rows;
    //get page count
    const page = pageState.page;
    
    // Update current page
    currentPage.value = page;
    
    // Update URL with all parameters
    updateUrlWithFilters();

    //calculate start index
    const startIndex = rows * page;
    //calculate end index
    const endIndex = startIndex + rows;

    //get the data for the current page
    paginatedData.value = lots.value.slice(startIndex, endIndex);
}

// Initialize filters from URL
onMounted(() => {
    lots.value = doFilter();

    paginatedData.value = lots.value.slice(currentPage.value * 10, (currentPage.value + 1) * 10);
});

const sortOptions = [
    { name: 'Nieuwste', sort: 'created-desc' },
    { name: 'Oudste', sort: 'created-asc' },
    { name: 'Prijs: Hoog tot laag', sort: 'price-desc' },
    { name: 'Prijs: Laag tot hoog', sort: 'price-asc' },
]
const filters = [
    {
        id: 'category',
        name: 'Categorie',
        options: props.categories.map(category => ({ value: category.id, label: category.name })),
    },
    {
        id: 'merk',
        name: 'Merk',
        options: props.brands.map(brand => ({ value: brand.name.toLowerCase(), label: brand.name })),
    },
    {
        id: 'mileage',
        name: 'KM-stand',
        options: [
            { value: 'to100', label: 'Tot 100.000km' },
            { value: 'to200', label: 'Tot 200.000km' },
            { value: 'over200', label: "Over 200.000km" },
        ],
    },
    {
        id: 'year',
        name: 'Bouwjaar',
        options: Array.from({ length: 50 }, (_, i) => {
            const year = new Date().getFullYear() - i
            return { value: year, label: year }
        }),
    },
    {
        id: 'transmission',
        name: 'Transmissie',
        options: [
            { value: 'handgeschakeld', label: 'Handmatig' },
            { value: 'semi-automaat', label: 'Semi-automaat' },
            { value: 'automaat', label: 'Automaat' },
        ],
    },
    {
        id: 'fuel',
        name: 'Brandstof',
        options: [
            { value: 'benzine', label: 'Benzine' },
            { value: 'diesel', label: 'Diesel' },
            { value: 'benzine-elektriciteit', label: 'Hybride benzine' },
            { value: 'diesel-elektriciteit', label: 'Hybride diesel' },
            { value: 'elektriciteit', label: 'Elektriciteit' },
            { value: 'lpg', label: 'LPG' },
        ],
    },
    {
        id: 'color',
        name: 'Kleur',
        options: [
            { label: "Rood", value: "rood" },
            { label: "Blauw", value: "blauw" },
            { label: "Groen", value: "groen" },
            { label: "Zwart", value: "zwart" },
            { label: "Wit", value: "wit" },
            { label: "Grijs", value: "grijs" },
            { label: "Geel", value: "geel" },
            { label: "Oranje", value: "oranje" },
            { label: "Paars", value: "paars" },
            { label: "Roze", value: "roze" },
            { label: "Bruin", value: "bruin" },
            { label: "Beige", value: "beige" },
            { label: "Turquoise", value: "turquoise" },
            { label: "Zilver", value: "zilver" },
            { label: "Goud", value: "goud" },
            { label: "Chroom", value: "chroom" },
            { label: "Bronze", value: "bronze" },
            { label: "Overig", value: "overig" },
        ],
    },
    {
        id: 'price',
        name: 'Hoogste bod', //TODO veranderen naar percentages
        options: [
            { value: 'to10', label: 'Tot 10%' },
            { value: 'to20', label: 'Tot 20%' },
            { value: 'to30', label: 'Tot 30%' },
            { value: 'to40', label: 'Tot 40%' },
            { value: 'to50', label: 'Tot 50%' },
            { value: 'to60', label: 'Tot 60%' },
            { value: 'to70', label: 'Tot 70%' },
            { value: 'to80', label: 'Tot 80%' },
            { value: 'to90', label: 'Tot 90%' },
            { value: 'to100', label: 'Tot 100%' },
        ],
    },
]
const mobileFiltersOpen = ref(false)

const showGrid = ref(false)
const showList = ref(true)

function getModel(sectionId) {
    switch (sectionId) {
        case 'category':
            return activeCategoryFilters
        case 'merk':
            return activeBrandFilters
        case 'mileage':
            return activeMileageFilters
        case 'year':
            return activeYearFilters
        case 'transmission':
            return activeTransmissionFilters
        case 'fuel':
            return activeFuelFilters
        case 'color':
            return activeColorFilters
        case 'price':
            return activePriceFilters
    }
}

watch([activeCategoryFilters, activeBrandFilters, activeMileageFilters, activeYearFilters, activeTransmissionFilters, activeFuelFilters, activeColorFilters, activePriceFilters], () => {
    //filter lots
    lots.value = doFilter();
    
    // Reset to first page when filters change and update URL
    currentPage.value = 0;
    updateUrlWithFilters();
    
    changePagination({ rows: 10, page: 0 });
})

function doFilter() {
    const filteredLots = pageLots.value.filter(lot => {
        if (activePriceFilters.value.length > 0) {
            // const price = parseInt(lot.price)
            //get the highest bid price
            const price = lot.bids.length > 0 ? lot.bids[0].amount : 0;
            if (lot.minimum_price == null || lot.highest_bid == null) return false;
            const percentage = Math.round((lot.highest_bid / lot.minimum_price) * 100);
            if (activePriceFilters.value.includes('to10') && percentage > 10) return false
            if (activePriceFilters.value.includes('to20') && percentage > 20) return false
            if (activePriceFilters.value.includes('to30') && percentage > 30) return false
            if (activePriceFilters.value.includes('to40') && percentage > 40) return false
            if (activePriceFilters.value.includes('to50') && percentage > 50) return false
            if (activePriceFilters.value.includes('to60') && percentage > 60) return false
            if (activePriceFilters.value.includes('to70') && percentage > 70) return false
            if (activePriceFilters.value.includes('to80') && percentage > 80) return false
            if (activePriceFilters.value.includes('to90') && percentage > 90) return false
            if (activePriceFilters.value.includes('to100') && percentage > 100) return false
        }

        // if (activeStatusFilters.value.length > 0) {
        //     if (!activeStatusFilters.value.includes(lot.status)) return false
        // }

        if (activeCategoryFilters.value.length > 0) {
            if (!activeCategoryFilters.value.includes(lot.category_id)) return false
        }

        if (activeBrandFilters.value.length > 0) {
            //check if brand is in active filters, ignore uppercase
            if (!activeBrandFilters.value.includes(lot.make.toLowerCase())) return false

        }

        if (activeMileageFilters.value.length > 0) {
            if (activeMileageFilters.value.includes('to100') && lot.mileage > 100000) return false
            if (activeMileageFilters.value.includes('to200') && lot.mileage > 200000) return false
            if (activeMileageFilters.value.includes('over200') && lot.mileage < 200000) return false
        }

        if (activeYearFilters.value.length > 0) {
            if (!activeYearFilters.value.includes(parseInt(lot.construction_year))) return false
        }

        if (activeTransmissionFilters.value.length > 0) {
            if (!activeTransmissionFilters.value.includes(lot.transmission)) return false
        }

        if (activeFuelFilters.value.length > 0) {
            if (!activeFuelFilters.value.includes(lot.fuel_type.toLowerCase())) return false
        }

        if (activeColorFilters.value.length > 0) {
            if (!activeColorFilters.value.includes(lot.color)) return false
        }

        return true
    });

    if (searchValue.value) {
        return filteredLots.filter(lot => {
            return lot.make.toLowerCase().includes(searchValue.value.toLowerCase()) || lot.model.toLowerCase().includes(searchValue.value.toLowerCase()) ||
            (lot.make.toLowerCase() + " " + lot.model.toLowerCase()).includes(searchValue.value.toLowerCase());
        });
    }

    return filteredLots;
}

function formatMileage(mileage) {
    return mileage.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ".")
}

const currentSortOption = computed(() => {
    // Remove debugging log that shows multiple triggers
    const sort = currentSort.value;

    if (sort) {
        // Use exact matching instead of includes to prevent partial matching issues
        const sortOption = sortOptions.find(option => option.sort === sort);
        return sortOption ? sortOption.name : 'Nieuwste';
    } else {
        return 'Nieuwste';
    }
});

function addToFavorites(id) {
    axios.get('/portal/kavel/' + id + '/favorite')
        .then(response => {
            //loop through all lots and set is_favorite to true if lot id is in response
            lots.value.forEach(lot => {
                if (lot.id === id) {
                    lot.is_favorite = true;
                }
            });
        })
        .catch(error => {
            console.log(error);
        });
}

function removeFromFavorites(id) {
    axios.get('/portal/kavel/' + id + '/unfavorite')
        .then(response => {
            //loop through all lots and set is_favorite to true if lot id is in response
            lots.value.forEach(lot => {
                if (lot.id === id) {
                    lot.is_favorite = false;
                }
            });
        })
        .catch(error => {
            console.log(error);
        });
}

function canSeeImage(lot) {
    //check if user is dealer
    if (user.dealer) {
        if (lot.status == 'accepted') return true;
        return false;
    }
    return true;
}

function getPercentageForBidPrice(minimum, highest_bid) {
    if (minimum == null || highest_bid == null) return '0%';
    //get the percentage of the highest bid compared to the minimum price
    return Math.round((highest_bid / minimum) * 100) + '%';
}

function calculateGradient(endDate) {
    //based on the end date calculate the gradient

    const now = moment();
    const end = moment(endDate).subtract(5, 'days');


    const minutes = end.diff(now, 'minutes');
    
    const days = (minutes / 60 / 24) * -1;
    //0 days = green
    //5 days = orange
    //14 days = red

    var color = '';

    if (days <= 0) {
        color = '00FF00';
    } else if (days <= 5) {
        color = interpolateColor('00FF00', 'FFA500', days / 5);
    } else if (days <= 14) {
        color = interpolateColor('FFA500', 'FF0000', (days - 5) / 9);
    } else {
        color = 'FF0000';
    }
    return '#' + color;
}

function interpolateColor(c0, c1, f){
    c0 = c0.match(/.{1,2}/g).map((oct)=>parseInt(oct, 16) * (1-f))
    c1 = c1.match(/.{1,2}/g).map((oct)=>parseInt(oct, 16) * f)
    let ci = [0,1,2].map(i => Math.min(Math.round(c0[i]+c1[i]), 255))
    return ci.reduce((a,v) => ((a << 8) + v), 0).toString(16).padStart(6, "0")
}

function changeSort(sort) {
    //get url params
    const urlParams = new URLSearchParams(window.location.search);
    //set sort param
    urlParams.set('sort', sort);
    
    // Update the reactive sort reference
    currentSort.value = sort;
    
    //update the url
    const newUrl = new URL(window.location.href);
    newUrl.search = urlParams.toString();

    router.visit(newUrl.pathname + newUrl.search, {
        replace: true,
    });
}
</script>

<template>
    <Head title="Actueel aanbod | Mijnautosnelweg" />
    <main class="px-4 sm:px-6 py-6 space-y-8">
        <div>
            <nav class="sm:hidden" aria-label="Back">
                <Link href="/portal/dashboard" class="flex items-center text-sm font-medium text-gray-500 hover:text-gray-700 gap-1">
                    <font-awesome-icon :icon="['fal', 'chevron-left']" class="h-4 w-4 shrink-0" />
                    Terug
                </Link>
            </nav>
            <nav class="hidden sm:flex" aria-label="Breadcrumb">
                <ol role="list" class="flex items-center space-x-4">
                    <li>
                        <div class="flex">
                            <Link href="/portal/dashboard"
                                class="text-sm font-medium text-gray-500 hover:text-gray-700">Dashboard</Link>
                        </div>
                    </li>
                    <font-awesome-icon :icon="['fal', 'chevron-right']" class="h-4 w-4 shrink-0" />
                    <li>
                        <div class="flex items-center">
                            <Link href="#" class="text-sm font-medium text-gray-900 hover:text-gray-700">
                            Aanbod</Link>
                        </div>
                    </li>
                </ol>
            </nav>
        </div>
        <div class="flex flex-col sm:flex-row justify-between w-full gap-y-2">
            <div class="flex flex-col">
                <h1 class="text-base font-semibold leading-6 text-gray-900">Aanbod</h1>
                <p class="mt-2 text-sm text-gray-700">Ons volledige aanbod.</p>
            </div>
            <div class="flex flex-row gap-3 items-center">
                <IconField iconPosition="right" class="w-full sm:w-auto">
                    <InputIcon class="pi pi-search"></InputIcon>
                    <InputText class="h-10" placeholder="Search" v-model="searchValue" />
                </IconField>
                <Link href="/portal/favorieten" class="">
                    <font-awesome-icon :icon="['far', 'heart']" class="p-2.5 h-5 w-5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 rounded-lg shrink-0"/>
                </Link>
            </div>
        </div>
        <div>
            <TransitionRoot as="template" :show="mobileFiltersOpen">
                <Dialog class="relative z-40 xl:hidden" @close="mobileFiltersOpen = false">
                    <TransitionChild as="template" enter="transition-opacity ease-linear duration-300"
                        enter-from="opacity-0" enter-to="opacity-100"
                        leave="transition-opacity ease-linear duration-300" leave-from="opacity-100"
                        leave-to="opacity-0">
                        <div class="fixed left-0 right-0 top-0 bottom-0 bg-black bg-opacity-5" />
                    </TransitionChild>
                    <div class="fixed left-0 right-0 top-0 bottom-0 z-40 flex">
                        <TransitionChild as="template" enter="transition ease-in-out duration-300 transform"
                            enter-from="translate-x-full" enter-to="translate-x-0"
                            leave="transition ease-in-out duration-300 transform" leave-from="translate-x-0"
                            leave-to="translate-x-full">
                            <DialogPanel
                                class="relative ml-auto flex h-full w-full max-w-xs bg-white flex-col overflow-y-auto  py-4 pb-6 shadow-xl">
                                <div class="flex items-center justify-between px-4">
                                    <h2 class="text-lg font-medium text-gray-900">Filters</h2>
                                    <button type="button"
                                        class="-mr-2 flex h-10 w-10 items-center justify-center rounded-md  p-2 text-gray-400 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-brightorange"
                                        @click="mobileFiltersOpen = false">
                                        <span class="sr-only">Close menu</span>
                                        <font-awesome-icon :icon="['far', 'xmark']" class="h-6 w-6 shrink-0"/>
                                    </button>
                                </div>

                                <form class="mt-4">
                                    <Disclosure as="div" v-for="section in filters" :key="section.name"
                                        class="border-t border-gray-200 px-4 py-6" v-slot="{ open }">
                                        <h3 class="-mx-2 -my-3 flow-root" v-if="section.id == 'price' && !user.dealer || section.id != 'price'">
                                            <DisclosureButton
                                                class="flex w-full items-center justify-between  px-2 py-3 text-sm text-gray-400">
                                                <span class="font-medium text-gray-900">{{ section.name }}</span>
                                                <span class="ml-6 flex items-center">
                                                    <font-awesome-icon :icon="['far', 'chevron-down']" rotation="90" :class="{ 'h-4 w-4 text-royalblue-500 fa-rotate': open, 'hidden': !open }" aria-hidden="true" />
                                                    <font-awesome-icon :icon="['far', 'chevron-right']" rotation="180" :class="{ 'hidden': open, 'h-4 w-4 text-royalblue-500': !open }" aria-hidden="true" />
                                                </span>
                                            </DisclosureButton>
                                        </h3>
                                        <DisclosurePanel class="pt-6">
                                            <div class="space-y-2">
                                                <div v-for="(option, optionIdx) in section.options" :key="option.value"
                                                    class="flex items-center">
                                                    <input :id="`filter-mobile-${section.id}-${optionIdx}`" v-model="getModel(section.id).value"
                                                    :value="option.value" :checked="getModel(section.id).value.includes(option.value)" type="checkbox" 
                                                        class="h-4 w-4 ring-1 ring-inset ring-gray-300 rounded border-gray-300 text-brightorange focus:ring-brightorange" />
                                                    <label :for="`filter-mobile-${section.id}-${optionIdx}`"
                                                        class="ml-3 text-sm text-gray-500">{{ option.label }}</label>
                                                </div>
                                            </div>
                                        </DisclosurePanel>
                                    </Disclosure>
                                </form>
                            </DialogPanel>
                        </TransitionChild>
                    </div>
                </Dialog>
            </TransitionRoot>

            <section aria-labelledby="filter-heading">
                <h2 id="filter-heading" class="sr-only">Product filters</h2>

                <div class="flex items-center justify-between">

                    <button type="button"
                        class="ring-1 ring-inset ring-gray-300 rounded-lg py-3.5 px-2.5 flex flex-row gap-2 text-sm font-medium text-gray-700 hover:text-gray-900 xl:hidden"
                        @click="mobileFiltersOpen = true">
                        <font-awesome-icon :icon="['far', 'filter']" class="h-5 w-5 shrink-0"/>

                        Filters
                    </button>

                    <PopoverGroup class="hidden xl:flex md:items-baseline sm:space-x-8">
                        <Popover as="div" v-for="(section, sectionIdx) in filters" :key="section.name" id="menu"
                            class="relative inline-block text-left">
                            <PopoverButton v-if="section.id == 'price' && !user.dealer || section.id != 'price'"
                                class="group inline-flex items-center justify-center text-sm font-medium text-gray-700 hover:text-gray-900 space-x-1">
                                <span>{{ section.name }}</span>
                                <span v-if="getModel(section.id).value.length > 0"
                                    class="ml-1.5 rounded bg-gray-200 px-1.5 py-0.5 text-xs font-semibold tabular-nums text-gray-700">{{getModel(section.id).value.length}}</span>
                                <font-awesome-icon :icon="['far', 'chevron-right']" rotation="90" class="h-4 w-4 text-royalblue-500" aria-hidden="true" />
                            </PopoverButton>

                            <transition enter-active-class="transition ease-out duration-100"
                                enter-from-class="transform opacity-0 scale-95"
                                enter-to-class="transform opacity-100 scale-100"
                                leave-active-class="transition ease-in duration-75"
                                leave-from-class="transform opacity-100 scale-100"
                                leave-to-class="transform opacity-0 scale-95">
                                <PopoverPanel
                                    class="absolute max-h-64 overflow-scroll bg-white border z-10 mt-2 origin-top-right rounded-md  p-4 shadow-2xl ring-1 ring-black ring-opacity-5 focus:outline-none">
                                    <form class="space-y-4">
                                        <div v-for="(option, optionIdx) in section.options" :key="option.value" 
                                            class="flex items-center">
                                            <input :id="`filter-${section.id}-${optionIdx}`" v-model="getModel(section.id).value"
                                                :value="option.value" :checked="getModel(section.id).value.includes(option.value)" type="checkbox" 
                                                class="h-4 w-4 ring-1 ring-inset ring-gray-300 rounded border-gray-300 text-brightorange focus:ring-brightorange" />
                                            <label :for="`filter-${section.id}-${optionIdx}`"
                                                class="ml-3 whitespace-nowrap pr-6 text-sm font-medium text-gray-900">{{
                                                option.label
                                                }}</label>
                                        </div>
                                    </form>
                                </PopoverPanel>
                            </transition>
                        </Popover>
                    </PopoverGroup>


                    <Menu as="div" class="relative inline-block text-left">
                        <div class="flex flex-row gap-2 items-center">

                            <MenuButton
                                class="group inline-flex justify-center items-center text-sm font-medium text-gray-700 hover:text-gray-900 space-x-1">
                                {{currentSortOption}}
                                <font-awesome-icon :icon="['fal', 'sort']" class="h-4 w-4 text-royalblue-500" aria-hidden="true" />
                            </MenuButton>
                            <div v-if="showList" class="relative w-11 h-11">
                                <button @click="showGrid = true; showList = false"
                                    class="absolute rounded-md bg-gray-200 p-3">
                                    <font-awesome-icon :icon="['far', 'table-cells-large']" class="h-5 w-5 shrink-0"/>
                                </button>
                            </div>
                            <div v-if="showList" class="relative w-11 h-11">
                                <button class="absolute rounded-md bg-brightorange p-3">
                                    <font-awesome-icon :icon="['far', 'table-list']" class="text-white h-5 w-5 shrink-0"/>
                                </button>
                            </div>
                            <div v-if="showGrid" class="relative w-11 h-11">
                                <button class="absolute rounded-md bg-brightorange p-3">
                                    <font-awesome-icon :icon="['far', 'table-cells-large']" class="text-white h-5 w-5 shrink-0"/>
                                </button>
                            </div>
                            <div v-if="showGrid" class="relative w-11 h-11">
                                <button @click="showGrid = false; showList = true"
                                    class="absolute rounded-md bg-gray-200 p-3">
                                    <font-awesome-icon :icon="['far', 'table-list']" class="h-5 w-5 shrink-0"/>
                                </button>
                            </div>
                        </div>

                        <transition enter-active-class="transition ease-out duration-100"
                            enter-from-class="transform opacity-0 scale-95"
                            enter-to-class="transform opacity-100 scale-100"
                            leave-active-class="transition ease-in duration-75"
                            leave-from-class="transform opacity-100 scale-100"
                            leave-to-class="transform opacity-0 scale-95">
                            <MenuItems
                                class="absolute bg-white right-0 border z-10 mt-2 w-48 origin-top-left rounded-md  shadow-2xl ring-1 ring-black ring-opacity-5 focus:outline-none">
                                <div class="">
                                    <MenuItem v-for="option in sortOptions">
                                        <button type="button" @click="changeSort(option.sort)"
                                            :class="[currentSort === option.sort ? 'bg-gray-100' : '', 'w-full block px-4 py-2 text-sm font-medium text-gray-900']">
                                            {{ option.name }}
                                        </button>
                                    </MenuItem>
                                </div>
                            </MenuItems>
                        </transition>
                    </Menu>
                </div>
            </section>
        </div>
        <div v-if="showList" class="space-y-8">
            <div class="mt-10 ring-1 ring-gray-300 rounded-lg w-full" v-if="paginatedData.length > 0">
                <table class="min-w-full divide-y divide-gray-300 border-b table-fixed">
                    <thead>
                        <tr>
                            <th scope="col"
                                class="hidden sm:table-cell px-3 py-3.5 flex-1 text-left text-sm font-semibold text-gray-900">
                                Kavel
                            </th>
                            <th scope="col" class="px-3 py-3.5 flex-1 text-left text-sm font-semibold text-gray-900">
                                Categorie</th>
                            <th scope="col" class="px-3 py-3.5 flex-1 text-left text-sm font-semibold text-gray-900 w-full max-w-72">
                                Merk &
                                model</th>
                            <th scope="col"
                                class="hidden lg:table-cell px-3 py-3.5 flex-1 text-left text-sm font-semibold text-gray-900">
                                Brandstof</th>
                            <th scope="col"
                                class="hidden lg:table-cell px-3 py-3.5 flex-1 text-left text-sm font-semibold text-gray-900">
                                KM-stand
                            </th>
                            <th scope="col"
                                class="hidden md:table-cell px-3 py-3.5 flex-1 text-left text-sm font-semibold text-gray-900">
                                Bouwjaar
                            </th>
                            <th scope="col"
                                class="hidden xl:table-cell px-3 py-3.5 flex-1 text-left text-sm font-semibold text-gray-900">
                                Transmissie
                            </th>
                            <th scope="col" class="px-3 py-3.5 flex-1 text-left text-sm font-semibold text-gray-900">
                                Hoogste bod
                            </th>
                            <th scope="col" class="hidden 2xl:table-cell px-3 py-3.5 flex-1 text-left text-sm font-semibold text-gray-900" v-if="!user.dealer">
                                Minimum prijs
                            </th>
                            <th scope="col" class="hidden 2xl:table-cell px-3 py-3.5 flex-1 text-left text-sm font-semibold text-gray-900" v-if="!user.dealer">
                                Percentage
                            </th>
                            <th scope="col" class="hidden 2xl:table-cell px-3 py-3.5 flex-1 text-left text-sm font-semibold text-gray-900">
                                Verkoper
                            </th>
                        </tr>
                    </thead>
                    <tbody class="divide-y">
                        <tr v-for="auto in paginatedData" class="relative">
                            <td
                                class="hidden sm:table-cell whitespace-nowrap py-4 pl-4 pr-3 text-sm font-medium text-gray-900 sm:pl-3">
                                <span v-if="!user.dealer" :style="'background-color: '+calculateGradient(auto.auction_end_date)" class="w-5 h-5 rounded-xl p-1 mr-3"></span>{{
                                auto.id }}
                            </td>
                            <td
                                class="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                                {{ auto.category ? auto.category.name : 'Geen categorie' }} 
                            </td>
                            <td
                                class="whitespace-nowrap px-3 py-4 text-sm text-gray-500 max-w-1 overflow-clip overflow-ellipsis">
                                {{ auto.make }} {{ auto.model
                                }}
                                {{ auto.body_type ?? '' }}
                            </td>
                            <td
                                class="hidden lg:table-cell whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                                {{ auto.fuel_type }}
                            </td>
                            <td class="hidden lg:table-cell whitespace-nowrap px-3 py-4 text-sm text-gray-500">{{ formatMileage(auto.mileage) }} km

                                <span v-if="auto.mileage_judgement == 'Logisch'" class="text-green-500"><font-awesome-icon :icon="['far', 'check']" /></span>
                                <span v-if="auto.mileage_judgement == 'Onlogisch'" class="text-red-500"><font-awesome-icon :icon="['far', 'xmark']" /></span>
                                <span v-if="auto.mileage_judgement != 'Logisch' && auto.mileage_judgement != 'Onlogisch'" class="text-orange-500 text-"><font-awesome-icon :icon="['far', 'question']" /></span>
                            </td>
                            <td class="hidden md:table-cell whitespace-nowrap px-3 py-4 text-sm text-gray-500">{{
                                auto.construction_year }}
                            </td>
                            <td class="hidden xl:table-cell whitespace-nowrap px-3 py-4 text-sm text-gray-500">{{
                                auto.transmission }}</td>
                            <td class="whitespace-nowrap px-3 py-4 text-sm text-gray-500">{{ auto.highest_bid ? eurFormatter.format(auto.highest_bid - auto.fee) : 'n.v.t.' }}</td>

                            <td v-if="!user.dealer" class="hidden 2xl:table-cell whitespace-nowrap px-3 py-4 text-sm text-gray-500">{{ auto.minimum_price ? eurFormatter.format(auto.minimum_price) : 'n.v.t.' }}</td>
                            <td v-if="!user.dealer" class="hidden 2xl:table-cell whitespace-nowrap px-3 py-4 text-sm text-gray-500">{{ getPercentageForBidPrice(auto.minimum_price, auto.highest_bid - auto.fee) }}</td>
                            <td class="hidden 2xl:table-cell whitespace-nowrap px-3 py-4 text-sm text-gray-500">{{ auto.seller_id ? auto.seller.full_name : 'n.v.t.' }}</td>

                            <td class="hidden lg:flex whitespace-nowrap py-4 pr-4 pl-3 text-right text-sm font-medium gap-1.5">
                                <Link :href="'/portal/kavel/' + auto.id"
                                    class="text-royalblue-500 hover:text-royalblue-500/80">
                                Bekijk
                                </Link>
                            </td>
                            <td class="flex lg:hidden whitespace-nowrap py-4 text-sm gap-1.5 pr-3">
                                <Link :href="'/portal/kavel/' + auto.id" class="text-royalblue-500 hover:text-royalblue-500/80">
                                    <font-awesome-icon :icon="['far', 'eye']" class="text-royalblue-500 hover:text-royalblue-500/80 h-5 w-5 shrink-0"/>
                                    <span class="sr-only">{{ auto.id }}</span>
                                </Link>
                            </td>
                        </tr>
                    </tbody>
                </table>
            </div>
            <div v-else class="flex justify-center">
                <div class="flex flex-col text-center my-auto">
                    <font-awesome-icon :icon="['far', 'car']" class="text-gray-400 h-12 w-12 mx-auto shrink-0"/>
                    <h3 class="mt-4 text-sm font-semibold text-gray-900">Geen kavels gevonden.</h3>
                </div>
            </div>

            

      <Paginator ref="paginator" v-if="lots.length > 10" @page="changePagination" :rows="10"
        :totalRecords="lots.length" :rowsPerPageOptions="[10, 20, 30]" :first="currentPage * 10"></Paginator>
        </div>

        <div v-if="showGrid" class="space-y-8">
            <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6" v-if="paginatedData.length > 0">
                <div v-for="lot in paginatedData" :key="lot.id"
                    class=" shadow-sm ring-1 ring-inset ring-gray-300 p-2 rounded-lg">
                    
                    <img v-if="lot.images.length > 0 && canSeeImage(lot)" :src="'/storage/vehicle_images/' + lot.images[0].path"
                        alt="Image" class="bg-black aspect-square object-cover rounded-lg min-h-[22rem]" />
                    <div v-else class="flex justify-center bg-black aspect-square object-cover rounded-lg text-white">
                        <div class="flex flex-col text-center my-auto">
                            <font-awesome-icon :icon="['far', 'images']" class="text-white h-12 w-12 mx-auto shrink-0"/>
                        <h3 class="mt-2 text-sm font-semibold">Geen afbeeldingen gevonden.</h3>
                        </div>
                    </div>
                    <div class="flex justify-between mt-4 items-center">
                        <p class="font-bold text-xl">{{ lot.make }} {{ lot.model }}</p>
                        <div class="flex flex-row gap-2 h-11">
                            <button class="">
                                <font-awesome-icon v-if="lot.is_favorite" @click="removeFromFavorites(lot.id)" :icon="['fas', 'heart']" class="p-2.5 h-5 w-5 shadow-sm ring-1 ring-inset ring-gray-300 bg-royalblue-500 hover:bg-royalblue-500/80 text-white rounded-lg shrink-0"/>
                                <font-awesome-icon v-else @click="addToFavorites(lot.id)" :icon="['far', 'heart']" class="p-2.5 h-5 w-5 shadow-sm ring-1 ring-inset ring-gray-300 text-gray-900 rounded-lg shrink-0"/>
                            </button>
                        </div>
                    </div>
                    <div class="flex flex-row justify-between pt-3">
                        <div class="flex flex-col">
                            <p>KM-stand: {{ formatMileage(lot.mileage) }} km</p>
                            <p>Bouwjaar: {{ lot.construction_year }}</p>
                            <p>Transmissie: {{ lot.transmission }}</p>
                            <p>Brandstof: {{ lot.fuel_type }}</p>
                        </div>
                        <div class="flex flex-col justify-end gap-2">
                            <p>Hoogste bod: <br><strong>{{ lot.highest_bid ? eurFormatter.format(lot.highest_bid) : 'Geen biedingen' }}</strong></p>
                            <Link :href="'/portal/kavel/' + lot.id">
                                <button type="button"
                                    class="w-full rounded-md bg-brightorange px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-brightorange/80 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-royalblue-500">
                                    Bekijk
                                </button>
                            </Link>
                        </div>
                    </div>
                </div>
            </div>
            <div v-else class="flex justify-center">
                <div class="flex flex-col text-center my-auto">
                    <font-awesome-icon :icon="['far', 'car']" class="text-gray-400 h-12 w-12 mx-auto shrink-0"/>
                    <h3 class="mt-4 text-sm font-semibold text-gray-900">Geen kavels gevonden.</h3>
                </div>
            </div>

            <Paginator ref="paginator" v-if="lots.length > 10" @page="changePagination" :rows="10"
        :totalRecords="lots.length" :rowsPerPageOptions="[10, 20, 30]" :first="currentPage * 10"></Paginator>
            
        </div>
    </main>
</template>
